import { config, type AdminIdentity, type TokenSet } from "./types";

const TOKENS = "megacorp-admin-tokens-v1";
const PKCE = "megacorp-admin-pkce-v1";
const REFRESH_TTL_MS = 30 * 24 * 60 * 60 * 1_000;

export function readTokens(): TokenSet | null {
  try {
    const raw = localStorage.getItem(TOKENS);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as TokenSet;
    if (!parsed.accessToken || !parsed.idToken || (parsed.refreshExpiresAtMs ?? parsed.expiresAtMs) <= Date.now()) return null;
    return parsed;
  } catch { return null; }
}

export function storeTokens(tokens: TokenSet | null): void {
  if (tokens) localStorage.setItem(TOKENS, JSON.stringify(tokens));
  else localStorage.removeItem(TOKENS);
}

export async function beginSignIn(): Promise<void> {
  const verifier = randomUrlSafe(64);
  const state = randomUrlSafe(32);
  sessionStorage.setItem(PKCE, JSON.stringify({ verifier, state }));
  const challenge = encode(await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier)));
  const url = new URL(`${trim(config.authDomain)}/oauth2/authorize`);
  url.searchParams.set("client_id", config.clientId);
  url.searchParams.set("response_type", "code");
  url.searchParams.set("redirect_uri", config.redirectUri);
  url.searchParams.set("scope", "openid email");
  url.searchParams.set("code_challenge_method", "S256");
  url.searchParams.set("code_challenge", challenge);
  url.searchParams.set("state", state);
  location.assign(url.toString());
}

export async function completeSignIn(): Promise<TokenSet | null> {
  if (config.authMode === "dev") return null;
  const url = new URL(location.href);
  const code = url.searchParams.get("code");
  if (!code) return null;
  const saved = JSON.parse(sessionStorage.getItem(PKCE) || "{}") as { verifier?: string; state?: string };
  if (!saved.verifier || !saved.state || saved.state !== url.searchParams.get("state")) throw new Error("Sign-in state did not match. Please try again.");
  const tokens = await tokenRequest({ grant_type: "authorization_code", client_id: config.clientId, code, redirect_uri: config.redirectUri, code_verifier: saved.verifier });
  sessionStorage.removeItem(PKCE);
  history.replaceState({}, "", "/");
  storeTokens(tokens);
  return tokens;
}

export async function refreshTokens(tokens: TokenSet): Promise<TokenSet> {
  if (!tokens.refreshToken) throw new Error("Your admin session has expired.");
  const next = await tokenRequest({ grant_type: "refresh_token", client_id: config.clientId, refresh_token: tokens.refreshToken }, tokens);
  storeTokens(next);
  return next;
}

export function signOut(): void {
  storeTokens(null);
  if (config.authMode === "dev") { location.assign("/"); return; }
  const url = new URL(`${trim(config.authDomain)}/logout`);
  url.searchParams.set("client_id", config.clientId);
  url.searchParams.set("logout_uri", config.logoutUri);
  location.assign(url.toString());
}

export function managePasskeys(): void {
  if (config.authMode === "dev") return;
  const url = new URL(`${trim(config.authDomain)}/passkeys/add`);
  url.searchParams.set("client_id", config.clientId);
  url.searchParams.set("redirect_uri", `${location.origin}/passkeys/complete`);
  location.assign(url.toString());
}

export async function activatePasskeyMfa(accessToken: string): Promise<void> {
  const response = await fetch(`https://cognito-idp.${config.region}.amazonaws.com/`, {
    method: "POST",
    headers: {
      "content-type": "application/x-amz-json-1.1",
      "x-amz-target": "AWSCognitoIdentityProviderService.SetUserMFAPreference",
    },
    body: JSON.stringify({
      AccessToken: accessToken,
      EmailMfaSettings: { Enabled: true, PreferredMfa: true },
      WebAuthnMfaSettings: { Enabled: true },
    }),
  });
  if (response.ok) return;
  const payload = await response.json().catch(() => ({})) as { message?: string };
  throw new Error(payload.message ?? "Cognito could not activate passkey sign-in.");
}

export function identityFromTokens(tokens: TokenSet | null): AdminIdentity | null {
  if (config.authMode === "dev") return { subject: "local-admin", email: "local-admin@megacorp.local", groups: ["megacorp-admin-operators"], capabilities: config.capabilities, role: "operator" };
  if (!tokens) return null;
  const claims = decodeJwt(tokens.idToken);
  const groups = Array.isArray(claims["cognito:groups"]) ? claims["cognito:groups"].filter((item): item is string => typeof item === "string") : [];
  return {
    subject: typeof claims.sub === "string" ? claims.sub : "unknown",
    email: typeof claims.email === "string" ? claims.email : null,
    groups,
    capabilities: groups.includes("megacorp-admin-operators")
      ? config.environment === "prod" ? ["admin:read", "admin:campaigns", "admin:mining", "admin:support", "admin:workers"]
        : ["admin:read", "admin:campaigns", "admin:mining", "admin:support", "admin:workers", "admin:devnet-grants"] : ["admin:read"],
    role: groups.includes("megacorp-admin-operators") ? "operator" : "viewer",
  };
}

async function tokenRequest(body: Record<string, string>, previous?: TokenSet): Promise<TokenSet> {
  const response = await fetch(`${trim(config.authDomain)}/oauth2/token`, { method: "POST", headers: { "content-type": "application/x-www-form-urlencoded" }, body: new URLSearchParams(body) });
  if (!response.ok) throw new Error("Cognito could not complete sign-in.");
  const payload = await response.json() as { access_token: string; id_token?: string; refresh_token?: string; expires_in: number };
  const now = Date.now();
  return { accessToken: payload.access_token, idToken: payload.id_token ?? previous?.idToken ?? "", refreshToken: payload.refresh_token ?? previous?.refreshToken, expiresAtMs: now + payload.expires_in * 1_000, refreshExpiresAtMs: previous?.refreshExpiresAtMs ?? now + REFRESH_TTL_MS };
}

function decodeJwt(token: string): Record<string, unknown> {
  try { return JSON.parse(atob(token.split(".")[1]!.replace(/-/g, "+").replace(/_/g, "/"))) as Record<string, unknown>; } catch { return {}; }
}
function trim(value: string): string { return value.replace(/\/$/, ""); }
function randomUrlSafe(length: number): string { const values = crypto.getRandomValues(new Uint8Array(length)); return encode(values); }
function encode(input: ArrayBuffer | Uint8Array): string { return btoa(String.fromCharCode(...new Uint8Array(input))).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, ""); }
